/**
 * 免拍地址抽查（监控侧）类型定义
 *
 * 对应表：pod_exempt_metric_daily / pod_exempt_spot_task / pod_exempt_spot_sample
 * plan：docs/plans/feature-team-pod-edge-case-report.md
 */

/** 抽查目标类型：单地址 / 围栏。与 hit_log、metric_daily、spot_task 共用 */
export enum ExemptTargetType {
    ADDRESS = 'ADDRESS',
    FENCE = 'FENCE',
}

/** 监控结论（metric_daily.monitor_level） */
export enum MonitorLevel {
    NORMAL = 'NORMAL',
    ATTENTION = 'ATTENTION',
    HIGH_RISK = 'HIGH_RISK',
}

/**
 * 抽查任务状态（spot_task.status）
 *
 * 两态无中间态：抽查人不预先分配、无领取动作，谁做就是谁。
 * 并发保护靠提交结论时的状态 CAS —— 两人同时查同一任务，后提交者被拒。
 */
export enum SpotTaskStatus {
    PENDING = 'PENDING',
    DONE = 'DONE',
}

/** 抽查任务来源（spot_task.source） */
export enum SpotTaskSource {
    MONITOR_ANOMALY = 'MONITOR_ANOMALY',
    RANDOM_SAMPLE = 'RANDOM_SAMPLE',
    MONTHLY_FENCE = 'MONTHLY_FENCE',
    MANUAL = 'MANUAL',
}

/**
 * 抽查结论（spot_task.conclusion）
 *
 * 不通过分两档，对豁免本身的效果相同（都下线），区别只在处罚力度：
 * OFFLINE 允许整改后重报，OFFLINE_BLOCKED 额外禁止该上报账号再申报。
 * 不设「转观察」「部分通过」。
 */
export enum SpotTaskConclusion {
    MAINTAIN = 'MAINTAIN',
    OFFLINE = 'OFFLINE',
    OFFLINE_BLOCKED = 'OFFLINE_BLOCKED',
}

/** 样本逐单判定结果（spot_sample.judge_result） */
export enum SampleJudgeResult {
    PENDING = 'PENDING',
    PASS = 'PASS',
    FAIL = 'FAIL',
}

/** 任务列表分组 Tab */
export enum SpotTaskTab {
    PENDING = 'PENDING',
    PASSED = 'PASSED',
    FAILED = 'FAILED',
}

/** 页面两个主 Tab */
export enum ExemptSpotCheckTab {
    TASKS = 'TASKS',
    MONITOR = 'MONITOR',
}

/**
 * 监控看板行：一行 = 一个生效目标（target）
 *
 * 展示字段（address/dsp/zipcode 等）来自主 R 的 I1 接口；
 * 指标字段来自本侧 metric_daily 的近 7 日聚合。
 */
export interface MonitorRow {
    targetType: ExemptTargetType;
    targetId: number;
    /** 展示编号，如 AD-2072 */
    targetNo: string;
    address: string;
    /** 副行：邮编 · 仓，围栏另附地址数 */
    zipcode?: string;
    warehouseCode?: string;
    /** 围栏内地址数；物化挂载完成前为空，展示「统计中」 */
    addressCount?: number;
    addressType?: string;
    dspName?: string;

    /** 近 7 日总单量 */
    orderCount7d?: number;
    /** 近 7 日客诉数 */
    complaintCount7d?: number;
    /** 客诉环比：近 7 日 vs 前 7 日，quality 侧 PodExemptThresholdEvaluator 计算并落库 */
    complaintCountDeltaRate?: number;

    /** 监控结论：取最新一天的 monitor_level，非聚合值 */
    monitorLevel?: MonitorLevel;
    /** 触发明细，如「未妥投率 7.8% 超阈值 5%」 */
    triggerDetail?: string;

    /** 本周期是否已建抽查任务；true 时「建抽查任务」置灰，下一周期恢复可建 */
    hasSpotTaskInPeriod: boolean;
}

/** 监控看板统计卡 */
export interface MonitorStats {
    /** 监控中生效地址数 = I1 清单计数 */
    monitoringCount: number;
    /** 按「最新一天」口径统计，与列表 monitorLevel 保持一致 */
    anomalyCount: number;
    /** 监控中地址近 7 日总单量，取数口径待数据侧确认 */
    orderCount7d?: number;
    /** 仅统计监控中的地址，不含全域 */
    complaintCount7d?: number;
}

/** 抽查任务列表行 */
export interface SpotTaskRow {
    id: number;
    /** 展示编号 SP-{id} */
    taskNo: string;
    targetType: ExemptTargetType;
    targetId: number;
    targetNo: string;
    address: string;
    zipcode?: string;
    addressCount?: number;

    /** 抽查周期，半月粒度，如 2026-P17（quality 侧 currentPeriodNo 生成，period_type=HALF_MONTH）*/
    periodNo: string;
    source: SpotTaskSource;
    /** 入池原因快照，如「未妥投率 7.8% ↑，超阈值 5%」。生成时写入，事后不可回算 */
    sourceDetail?: string;

    status: SpotTaskStatus;
    /** 计划抽样单数，随 Q5 配置化 */
    planSampleCount?: number;
    /** 实际抽样数 / 不通过数，由 spot_sample 聚合回写 */
    sampleCount?: number;
    failCount?: number;
    /** 近 30 天豁免单量（经 I2 取得） */
    exemptCount?: number;

    /** 抽查人，提交结论时记录当前登录人；未完成时为空 */
    assigneeId?: number;
    assigneeName?: string;
    doneAt?: string;

    conclusion?: SpotTaskConclusion;
    conclusionRemark?: string;
}

/** 抽查任务统计卡 */
export interface SpotTaskStats {
    /** 本期待抽查 */
    pendingCount: number;
    /** 监控异常入池 */
    monitorAnomalyCount: number;
    /** 随机抽样入池 */
    randomSampleCount: number;
    /**
     * 不通过 → 下线数
     * 原型为「下线/重罚 14/6」，罚款链路不在方案范围，此处只显示下线数
     */
    offlineCount: number;
}

/** 抽查样本明细（执行页逐单判定） */
export interface SpotSampleRow {
    id: number;
    taskId: number;
    /** 被抽查订单 ID；用订单 ID 而非运单号，因运单号跨阶段不唯一 */
    orderId: number;
    /** 运单号快照，抽查人据它检索与沟通 */
    waybillNo?: string;
    judgeResult: SampleJudgeResult;
    /** POD 缩略图时效 URL，由后端按 orderId 实时签发；为空时卡片展示占位 */
    /** POD 图片列表，一单多张；presign URL 10 分钟过期，不缓存 */
    podImageUrls: string[];
    /** 投递司机与投递时间，判定时的辅助信息 */
    driverName?: string;
    deliveredAt?: string;
    judgeRemark?: string;
    judgedBy?: number;
    judgedByName?: string;
    judgedAt?: string;
}

/** 抽查执行页详情 */
export interface SpotTaskDetail {
    task: SpotTaskRow;
    samples: SpotSampleRow[];
}

/** 监控看板筛选条件 */
export interface MonitorFilters {
    targetType?: ExemptTargetType;
    monitorLevel?: MonitorLevel;
    dspId?: number;
    zipcode?: string;
    addressType?: string;
}

/** 抽查任务筛选条件 */
export interface SpotTaskFilters {
    periodNo?: string;
    source?: SpotTaskSource;
    targetType?: ExemptTargetType;
    assigneeId?: number;
    dspId?: number;
}
